import { useState, useEffect } from "react";
import { MessageSquare, Plus, Search, Loader2, Eye, MessageCircle, Send } from "lucide-react";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface ForumPost {
  id: string;
  title: string;
  content: string;
  author_id: string;
  skill_category: string | null;
  views_count: number;
  replies_count: number;
  created_at: string;
}

const CommunityForums = () => {
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newPost, setNewPost] = useState({
    title: "",
    content: "",
    skill_category: ""
  });
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('community_forums')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error: any) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const createPost = async () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to create posts",
        variant: "destructive"
      });
      return;
    }

    if (!newPost.title.trim() || !newPost.content.trim()) {
      toast({
        title: "Required fields",
        description: "Please fill in title and content",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('community_forums')
        .insert({
          ...newPost,
          author_id: user.id
        });

      if (error) throw error;

      toast({
        title: "Post created!",
        description: "Your discussion is now live 💬"
      });

      setCreateOpen(false);
      setNewPost({
        title: "",
        content: "",
        skill_category: ""
      });
      fetchPosts();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const filteredPosts = posts.filter(post =>
    post.title.toLowerCase().includes(search.toLowerCase()) ||
    post.content.toLowerCase().includes(search.toLowerCase()) ||
    post.skill_category?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <Navigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="gradient-primary text-white p-6 pb-12 rounded-b-3xl shadow-strong">
        <div className="container max-w-md mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <MessageSquare className="h-8 w-8" />
              <div>
                <h1 className="text-2xl font-bold">Community Forums</h1>
                <p className="text-sm text-white/80">Ask, share, and learn together</p>
              </div>
            </div>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button variant="secondary" size="icon">
                  <Plus className="h-5 w-5" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Discussion</DialogTitle>
                  <DialogDescription>
                    Start a conversation with the community
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      placeholder="What's your question or topic?"
                      value={newPost.title}
                      onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="content">Content *</Label>
                    <Textarea
                      id="content"
                      placeholder="Share your thoughts..."
                      rows={5}
                      value={newPost.content}
                      onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Category (optional)</Label>
                    <Input
                      id="category"
                      placeholder="e.g., Programming, Design, Music"
                      value={newPost.skill_category}
                      onChange={(e) => setNewPost({ ...newPost, skill_category: e.target.value })}
                    />
                  </div>
                  <Button onClick={createPost} className="w-full gradient-primary text-white">
                    <Send className="h-4 w-4 mr-2" />
                    Post Discussion
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/60" />
            <Input
              placeholder="Search discussions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white/20 border-white/30 text-white placeholder:text-white/60"
            />
          </div>
        </div>
      </header>

      <main className="container max-w-md mx-auto px-4 -mt-6 space-y-4">
        {filteredPosts.length === 0 ? (
          <Card className="p-8 text-center shadow-soft">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-bold text-lg mb-2">No Discussions Yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {search ? "Try a different search term" : "Be the first to start a conversation!"}
            </p>
            <Button onClick={() => setCreateOpen(true)} variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Create Post
            </Button>
          </Card>
        ) : (
          filteredPosts.map((post) => (
            <Card key={post.id} className="p-5 shadow-soft hover:shadow-md transition-shadow cursor-pointer">
              <div className="space-y-3">
                <div>
                  <h3 className="font-bold text-lg mb-1">{post.title}</h3>
                  {post.skill_category && (
                    <Badge variant="secondary" className="mb-2">
                      {post.skill_category}
                    </Badge>
                  )}
                </div>

                <p className="text-sm text-muted-foreground line-clamp-2">
                  {post.content}
                </p>

                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      <span>{post.views_count}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MessageCircle className="h-3 w-3" />
                      <span>{post.replies_count}</span>
                    </div>
                  </div>
                  <span>{formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</span>
                </div>
              </div>
            </Card>
          ))
        )}
      </main>

      <Navigation />
    </div>
  );
};

export default CommunityForums;
